import { format, parseISO } from 'date-fns';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import Button from 'devextreme-react/button';
import DataGrid, { Column, ColumnChooser, Editing, FilterRow, Item, Paging, Sorting, Toolbar, RemoteOperations, Scrolling } from 'devextreme-react/data-grid';
import { Popup } from 'devextreme-react/popup';
import { Tooltip } from 'devextreme-react/tooltip';
import { InitializedEventInfo, one } from 'devextreme/events';
import dxTooltip from 'devextreme/ui/tooltip.js';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import grantApi from '../../api/GrantApi.js';
import sharedApi from '../../api/SharedApi.js';
import userApi from '../../api/UserApi.js';
import '../css/Portfolio.css';
import GrantDetailCommentTooltip from '../grantDetails/GrantDetailCommentTooltip';
import GrantDetailPreAwardTaskTooltip from '../grantDetails/GrantDetailPreAwardTaskTooltip';
import { useAppContext } from '../shared/AppContext';
import RiskMeter, { RiskMeterPresets } from '../shared/riskMeter/RiskMeter';
import { getBufferOverallColor } from '../shared/riskMeter/utils/riskMeterColors';
import StatusIcon from '../shared/StatusIcon';
import { GRANTSTATUS, ROLE, getPhaseStatusInfo, getStepStatusInfo, getUserFullName } from '../shared/Utils';
import PortfolioGridCell from './PortfolioGridCell';
import { RiskMeterDto } from '../../dtos/RiskMeterDto';

// Self-contained component for risk meter cell to avoid grid-wide re-renders
function PortfolioRiskMeterCell({ riskMeter, grantId }: { riskMeter: RiskMeterDto; grantId: number }) {
    const tooltipId = `PortfolioRiskMeter${grantId}`;
    const overallScore = riskMeter.overallScore;
    const riskColor = getBufferOverallColor(overallScore);
    const riskText = getRiskLevelText(overallScore);

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <a
                id={tooltipId}
                href={`/grantdetails/${grantId}#riskmeter`}
                style={{ display: 'inline-block' }}
            >
                <RiskMeter data={riskMeter} {...RiskMeterPresets.icon} />
            </a>
            <Tooltip
                target={`#${tooltipId}`}
                position="right"
                showEvent={{ name: "mouseenter", delay: 300 }}
                hideEvent={{ name: "mouseleave", delay: 50 }}
                contentRender={() => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 6px' }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: riskColor
                            }}
                        />
                        <span>{overallScore.toFixed(1)} - {riskText}</span>
                    </div>
                )}
            />
        </div>
    );
}

// Maps overall risk score to descriptive text (matches BufferOverallExplanations)
function getRiskLevelText(score: number): string {
    if (score <= -1.5) return 'Low risk';
    if (score <= -0.5) return 'Moderate risk';
    if (score <= 0.5) return 'Medium risk';
    if (score <= 1.5) return 'High risk';
    return 'Very high risk';
}

const dsAwardsAndApplications = (userId: string) => {
    return createStore({
        key: 'id',
        loadUrl: sharedApi.generateURL('api/portfolio/awards-and-applications'),
        updateUrl: sharedApi.generateURL('api/portfolio/awards'),
        onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.xhrFields = { withCredentials: true };
            ajaxOptions.headers = { 'Authorization': 'Bearer ' + userApi.getAccessToken() };
        }
    });
}

const dsAwards = (userId: string) => {
    return createStore({
        key: 'id',
        loadUrl: sharedApi.generateURL('api/portfolio/awards'),
        updateUrl: sharedApi.generateURL('api/portfolio/awards'),
        onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.xhrFields = { withCredentials: true };
            ajaxOptions.headers = { 'Authorization': 'Bearer ' + userApi.getAccessToken() };
        }
    });
}

const dsApplications = (userId: string) => {
    return createStore({
        key: 'id',
        loadUrl: sharedApi.generateURL('api/portfolio/applications'),
        updateUrl: sharedApi.generateURL('api/portfolio/applications'),
        onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.xhrFields = { withCredentials: true };
            ajaxOptions.headers = { 'Authorization': 'Bearer ' + userApi.getAccessToken() };
        }
    });
}

interface IPortfolioGridProps {
    grantStatus?: number;
}

export default function PortfolioGrid({ grantStatus }: IPortfolioGridProps) {
    const app = useAppContext();

    const portfolioGrid = useRef<DataGrid<any, string>>(null);

    const [state, setState] = useState({
        loading: true,
        loadingReport: false,
        grants: [],
    });

    const renderGrantLastActivity = (cellData: any) => {
        if (cellData.data.status !== GRANTSTATUS.AWARDED) {
            if (cellData.data.latestPreAwardDetail) {
                var lastTask = cellData.data.latestPreAwardDetail;
                var statusDate = lastTask.date
                return (statusDate !== null) ? moment(statusDate).format('YYYY-MM-DD') : '';
            }
        }
        else {
            return (cellData.data.lastActivityDate !== null) ? moment(cellData.data.lastActivityDate).format('YYYY-MM-DD') : '';
        }
    }

    const renderGrantStatus = (cellData: any) => {
        var statusNumber = cellData.data.status;
        var statusText = "";

        switch (statusNumber) {
            case GRANTSTATUS.AWARDED:
                statusText = "Awarded";
                break;
            case GRANTSTATUS.APPLICATION:
                statusText = "Pending Award";
                break;
            case GRANTSTATUS.COUNCILREVIEW:
                statusText = "Council Review";
                break;
            case GRANTSTATUS.TOBEPAID:
                statusText = "To be Paid";
                break;
            case GRANTSTATUS.AWARDPREPARED:
                statusText = "Award prepared, not funded";
                break;
            default:
                statusText = "*Unknown Status*";
                break;
        }

        return (
            statusText
        );
    };

    // non-tasklist - use this logic instead of config permissions
    const allowEditingTitle = () => {
        return (app.userRole == ROLE.PO || app.userRole == ROLE.PI || app.userRole == ROLE.AOR || app.userRole == ROLE.SU);
    };

    const renderGrantNumber = (cellData: any) => {
        var cellText = cellData.data.grantNumber;

        return (
            <div>
                <span className='GrantNumber'>{cellText}</span>
            </div>
        );
    };

    const getGrantReport = async (id: string, value: string) => {
        await grantApi.getGrantReport(id, value);
        setState({ ...state, loadingReport: false });
    }

    const onCellClick = async (e: any) => {
        if (e.columnIndex === 1 && e.rowType !== "filter" && e.rowType !== "header") {
            setState({ ...state, loadingReport: true });
            await getGrantReport(e.data.id, e.value);
        }
    }

    const isConsolidatedView = () => {
        return grantStatus == null;
    }

    const portfolioDataSet = () => {
        if (isConsolidatedView()) {
            return dsAwardsAndApplications(app.userId);
        } else {
            return (grantStatus === GRANTSTATUS.AWARDED) ? dsAwards(app.userId) : dsApplications(app.userId);
        }
    }

    const showAwardColumn = () => {
        if (isConsolidatedView() || grantStatus === GRANTSTATUS.AWARDED)
            return true;
        else
            return false;
    }

    // Risk column shown for Awards tab for all users
    const showRiskColumn = grantStatus === GRANTSTATUS.AWARDED;

    const renderRiskMeter = (cellData: any) => {
        const riskMeter = cellData.data.riskMeter as RiskMeterDto | null;
        const grantId = cellData.data.id;

        if (!riskMeter || !riskMeter.slices || riskMeter.slices.length === 0) {
            return <span style={{ color: '#666' }}>-</span>;
        }

        return <PortfolioRiskMeterCell riskMeter={riskMeter} grantId={grantId} />;
    };

    const activityRender = (e: any) => {
        const lastCommentDate = moment(e.data.lastComment ? e.data.lastComment.dateUpdated : 0);
        const lastFileUploadDate = moment(e.data.lastFileUpload ? e.data.lastFileUpload.creationDate : 0);

        if (e.data.status !== GRANTSTATUS.AWARDED) {
            if (e.data.latestPreAwardDetail) {
                var lastTask = e.data.latestPreAwardDetail;
                var statusTask = lastTask.task;
                var statusTaskInfo = getPhaseStatusInfo(lastTask.taskStatus, lastTask.taskDueDate);
                var statusSubTask = lastTask.subTask
                var statusSubTaskInfo = getStepStatusInfo(lastTask.subTaskStatus, lastTask.subTaskDueDate);
                var statusUser = lastTask.user
                var statusDate = lastTask.date
                if (statusUser && statusDate)
                    return (
                        <div>
                            <div id={`PortfolioTaskStatusDetail${e.data.id}`} >
                                <strong>{statusTask} </strong>
                                <StatusIcon info={statusTaskInfo} diameter={15} displayInline={true} ></StatusIcon>
                                {" > "}
                                <strong>{statusSubTask} </strong>
                                <StatusIcon info={statusSubTaskInfo} diameter={15} displayInline={true} ></StatusIcon>
                            </div>
                            <GrantDetailPreAwardTaskTooltip
                                taskName={statusTask}
                                taskStatus={statusTaskInfo.text}
                                subTaskName={statusSubTask}
                                subTaskStatus={statusSubTaskInfo.text}
                                commentUser={getUserFullName(statusUser)}
                                commentDate={format(parseISO(statusDate), 'MM/dd/yyyy h:mm a')}
                                target={`PortfolioTaskStatusDetail${e.data.id}`}
                            />
                        </div>
                    )
                else
                    return <></>;
            }
            else
                return <></>;
        }

        return (lastCommentDate > lastFileUploadDate && e.data.lastComment) ?
            <div>
                <div id={`PortfolioLastComment${e.data.id}`} ><i className="dx-icon-comment"></i> {e.data.lastComment.comment}</div>
                <GrantDetailCommentTooltip
                    commentText={e.data.lastComment.comment}
                    commentUser={getUserFullName(e.data.lastComment.updatedByUser)}
                    commentDate={format(parseISO(e.data.lastComment.dateUpdated), 'MM/dd/yyyy h:mm a')}
                    target={`PortfolioLastComment${e.data.id}`}
                />
            </div> :
            (e.data.lastFileUpload) ?
                <div>
                    <div><i className="dx-icon-file"></i> {e.data.lastFileUpload.phaseName + ' > '}{e.data.lastFileUpload.stepName ? e.data.lastFileUpload.stepName + ' > ' : ''} {e.data.lastFileUpload.name}</div>
                    <Tooltip hideOnOutsideClick={true} showEvent={{ name: "dxhoverstart", delay: 500 }} hideEvent={{ name: 'dxhoverend', delay: 50 }} onHiding={(e: any) => {
                        e.cancel = new Promise(cancel => {
                            setTimeout(() => {
                                const popupContainer = e.component.content().parentElement;
                                const isHover = popupContainer.parentNode.matches(':hover');
                                if (isHover) {
                                    one(popupContainer, 'mouseleave', () => {
                                        cancel(false);
                                    });
                                } else {
                                    cancel(false);
                                }
                            }, 50);
                        })
                    }}
                        position="bottom" onInitialized={(e: InitializedEventInfo<dxTooltip>) => { e.component?.option('target', e.element?.previousSibling as Element); }}>
                        <div className="phase-comment-popup">
                            <p>File: {e.data.lastFileUpload.name}</p>
                            <p>Phase: {e.data.lastFileUpload.phaseName}</p>
                            {e.data.lastFileUpload.stepName ? <p>Step: {e.data.lastFileUpload.stepName}</p> : <></>}
                            <p>User: {getUserFullName(e.data.lastFileUpload.user)}</p>
                            <p>Date: {format(parseISO(e.data.lastFileUpload.creationDate), 'MM/dd/yyyy h:mm a')}</p>
                        </div>
                    </Tooltip>
                </div>
                :
                <></>;
    }

    const renderCustomItem = () => {
        return <h4>Grants</h4>;
    }

    return (
        <div>
            <Popup
                visible={state.loadingReport}
                dragEnabled={false}
                position="center"
                hideOnOutsideClick={false}
                showCloseButton={false}
                showTitle={false}
                width={220}
                height={100}>
                <div className="row">
                    <div className="col-lg-12" style={{ marginTop: '15px', marginLeft: '60px' }}>
                        <img src='Loading.gif' alt='Loading...' style={{ marginLeft: '15px', height: '30px', width: '30px' }} />
                        <div style={{ fontSize: '12pt' }} >Loading...</div>
                    </div>
                </div>
            </Popup>
            <DataGrid
                id="portfolioGrid"
                elementAttr={showRiskColumn ? { class: 'portfolio-awards' } : undefined}
                ref={portfolioGrid}
                height={window.innerHeight - 200}
                dataSource={portfolioDataSet()}
                showColumnLines={false}
                showRowLines={true}
                showBorders={true}
                rowAlternationEnabled={true}
                /*onEditorPrepared={onEditorPrepared}
                onCellPrepared={onCellPrepared}*/
                onCellClick={(e) => onCellClick(e)}
                remoteOperations={true}
                repaintChangesOnly={true}
                customizeColumns={(columns) => {
                    columns.forEach((col: any) => {
                        col.cssClass = 'vertical-center';
                    });
                }}
            >
                <RemoteOperations
                    filtering={true}
                    paging={true}
                    sorting={true}
                    summary={true}
                    grouping={true}
                    groupPaging={true}
                />
                <Scrolling mode="virtual" />
                <ColumnChooser enabled={true} mode="select" />
                <FilterRow visible={true} applyFilter={"auto"} />
                <Editing
                    mode="cell"
                    allowUpdating={true}
                />
                <Paging enabled={false} />
                <Sorting mode="multiple" />

                <Toolbar visible={isConsolidatedView()}>
                    <Item
                        name="portfolioGridHeader"
                        location="before"
                        render={renderCustomItem}
                    ></Item>
                    <Item name="columnChooserButton"
                        location="after"
                    >
                        <Button
                            icon="columnchooser"
                            text="Column Chooser"
                            hint="Show/Hide Columns"
                            onClick={() => {
                                portfolioGrid?.current?.instance?.showColumnChooser();
                            }}
                        />
                    </Item>
                </Toolbar>
                <Column
                    width={'124px'}
                    dataField="applicationId"
                    visible={false}
                />
                <Column
                    dataField="fy"
                    width={'45px'}
                    allowEditing={false}
                    sortIndex={0}
                    sortOrder="desc"
                    caption="FY" />
                <Column
                    dataField="grantNumber"
                    width={'125px'}
                    allowEditing={false}
                    caption="Grant #"
                    cellRender={renderGrantNumber} />
                <Column
                    dataField="shortInstitution"
                    width={'285px'}
                    caption="Institution"
                    allowEditing={false}
                    sortIndex={1}
                    sortOrder="asc"
                    cellRender={PortfolioGridCell} />
                <Column
                    dataField="shortTitle"
                    allowEditing={allowEditingTitle()}
                    caption="Project Title"
                    visible={false}
                />
                {showRiskColumn && (
                    <Column
                        dataField="riskScore"
                        width={'80px'}
                        allowEditing={false}
                        allowFiltering={false}
                        allowSorting={true}
                        alignment="center"
                        caption="Risk"
                        cellRender={renderRiskMeter}
                    />
                )}
                <Column
                    dataField="lastActivityDate"
                    width={'120px'}
                    allowEditing={false}
                    caption="Last Activity"
                    cellRender={renderGrantLastActivity} />
                <Column
                    allowEditing={false}
                    allowFiltering={false}
                    allowSorting={false}
                    caption="Details"
                    cellRender={activityRender} />

                <Column
                    dataField="pi"
                    width={'125px'}
                    allowEditing={false}
                    cellRender={PortfolioGridCell}
                    caption="PI"
                    visible={false} />
                <Column
                    dataField="aor"
                    width={'125px'}
                    allowEditing={false}
                    cellRender={PortfolioGridCell}
                    caption="SO"
                    visible={false} />
                <Column
                    dataField="po"
                    width={'125px'}
                    allowEditing={false}
                    cellRender={PortfolioGridCell}
                    caption="PO" />
                <Column
                    dataField="gms"
                    width={'125px'}
                    allowEditing={false}
                    cellRender={PortfolioGridCell}
                    caption="GMS" />
                <Column
                    dataField="status"
                    width={'120px'}
                    allowEditing={false}
                    caption="Status"
                    visible={!showRiskColumn}
                    cellRender={renderGrantStatus} />
            </DataGrid>
        </div>
    );
}